import type { MessagePathRecord, TopicRecord } from "../topics";

import type { InvalidSignal, SignalEntry } from "./signalValidation";

/**
 * The record lookups {@link resolveSignalEntries} needs. `TopicService`
 * satisfies this; tests can pass a two-method fake.
 */
export interface SignalEntrySource {
  getMessagePathById(messagePathId: string): Promise<MessagePathRecord>;
  getTopicById(topicId: string): Promise<TopicRecord>;
}

export interface ResolvedSignalEntries {
  /** Successfully resolved entries, in input order. */
  entries: SignalEntry[];
  /**
   * Ids whose records could not be loaded, with per-id reasons. A stale or
   * mistyped id is the most common caller mistake, so every failing id is
   * reported — not just the first.
   */
  invalid: InvalidSignal[];
}

function reasonFromRejection(reason: unknown): string {
  return reason instanceof Error ? reason.message : String(reason);
}

/**
 * Fetch the message path + topic record for every id, deduping topic fetches
 * across paths that share a topic. Never throws for per-id failures; they
 * come back in `invalid`, leaving the caller to decide whether to fail
 * atomically (AI chat's panel tools) or keep the survivors (event
 * visualization).
 */
export async function resolveSignalEntries(
  source: SignalEntrySource,
  messagePathIds: string[],
): Promise<ResolvedSignalEntries> {
  const invalid: InvalidSignal[] = [];

  const messagePathResults = await Promise.allSettled(
    messagePathIds.map((id) => source.getMessagePathById(id)),
  );
  const messagePaths: MessagePathRecord[] = [];
  messagePathResults.forEach((result, i) => {
    if (result.status === "fulfilled") {
      messagePaths.push(result.value);
    } else {
      invalid.push({
        messagePathId: messagePathIds[i],
        reason: reasonFromRejection(result.reason),
      });
    }
  });

  const topicIds = [...new Set(messagePaths.map((mp) => mp.topic_id))];
  const topicResults = await Promise.allSettled(
    topicIds.map((id) => source.getTopicById(id)),
  );
  const topicsById = new Map<string, TopicRecord>();
  const topicFailures = new Map<string, string>();
  topicResults.forEach((result, i) => {
    if (result.status === "fulfilled") {
      topicsById.set(topicIds[i], result.value);
    } else {
      topicFailures.set(topicIds[i], reasonFromRejection(result.reason));
    }
  });

  const entries: SignalEntry[] = [];
  for (const messagePath of messagePaths) {
    const topic = topicsById.get(messagePath.topic_id);
    if (topic) {
      entries.push({ messagePath, topic });
    } else {
      invalid.push({
        messagePathId: messagePath.message_path_id,
        reason: `Failed to load its topic: ${
          topicFailures.get(messagePath.topic_id) ?? "topic not found"
        }`,
      });
    }
  }

  return { entries, invalid };
}
